import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { trpc } from "@utils/api";
import { type inferRouterOutputs, type inferRouterInputs } from "@trpc/server";
import { type AppRouter } from "@server/api/root";
import { TeamMember } from "../account/workplaceSlice";
import { generateId } from "skedyul-core/tools";

import { GetContactOutputType } from "skedyul-core/features/conversations";
import { z } from "zod/v4";
type RouterInput = inferRouterInputs<AppRouter>;
type RouterOutput = inferRouterOutputs<AppRouter>;

export type SendMessageType = RouterInput["chat"]["sendMessage"];
export type SaveAgentMessageParams = RouterInput["chat"]["sendMessage"];
export type SearchPhoneNumberParams = RouterInput["chat"]["getPhoneNumbers"];
export type SearchMessageParams = RouterInput["chat"]["getContact"];

export type ListMessage =
  RouterOutput["chat"]["getContact"]["messages"][number];

export type AgentMessage = {
  createdAt: string;
  content: string;
  role: "assistant" | "user";
};

export interface contactState {
  contacts: {
    isLoading: boolean;
    data: GetContactOutputType[];
    query: {
      q: string;
      limit: number;
      cursorId?: string | null;
      total: number;
    };
  };
  contact: {
    isLoading: boolean;
    data: GetContactOutputType | null;
    newMessage: {
      data: ListMessage | null;
      isSending: boolean;
      isLoading?: boolean;
      resetKey?: number;
    };
    stagedMessage: {
      message: string;
      isVisible: boolean;
      isLoading: boolean;
    };
    suggestedResponse: {
      message: string;
      isVisible: boolean;
      isLoading: boolean;
      selectedModelId: string | null;
      error: string | null;
      lastMessageId: string | null;
    };
  };
}

const initialState: contactState = {
  contacts: {
    isLoading: true,
    data: [],
    query: {
      q: "",
      limit: 25,
      cursorId: null,
      total: 0,
    },
  },
  contact: {
    isLoading: true,
    data: null,
    stagedMessage: {
      message: "",
      isVisible: false,
      isLoading: false,
    },
    suggestedResponse: {
      message: "",
      isVisible: false,
      isLoading: false,
      selectedModelId: null,
      error: null,
      lastMessageId: null,
    },
    newMessage: {
      data: null,
      isSending: false,
      resetKey: 0,
    },
  },
};

const shuffleContactToTop = (
  state: contactState,
  contact: GetContactOutputType,
  increment: boolean,
) => {
  const existing = state.contacts.data.find(
    (c) => c.id === contact.id,
  ) as unknown as (GetContactOutputType & { unreadCount?: number }) | undefined;
  const baseUnread =
    (existing?.unreadCount ??
      (contact as unknown as { unreadCount?: number }).unreadCount ??
      0) ||
    0;
  const nextUnread = increment ? baseUnread + 1 : 0;

  state.contacts.data = [
    {
      ...(contact as unknown as Record<string, unknown>),
      unreadCount: nextUnread as unknown as number,
    } as unknown as GetContactOutputType,
    ...(state.contacts.data
      .filter((c) => c.id !== contact.id)
      .map((c) => ({
        ...(c as unknown as Record<string, unknown>),
      })) as unknown as GetContactOutputType[]),
  ];
};

export const generateMessage = createAsyncThunk(
  "chat/generateMessage",
  async (
    payload: {
      prompt: string;
      communicationChannelId: string;
      contactId: string;
    },
    { dispatch },
  ) => {
    // Set up staged message state
    dispatch(
      updateStreamingMessage({
        message: "",
        done: false,
      }),
    );

    // Then start streaming
    const streamResponse = await fetch("/api/ai/generate-text", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!streamResponse.ok) {
      throw new Error("Failed to start streaming");
    }

    const reader = streamResponse.body?.getReader();
    if (!reader) {
      throw new Error("No reader available");
    }

    const decoder = new TextDecoder();
    let buffer = "";

    // Process the stream
    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete lines
      const lines = buffer.split("\n");
      buffer = lines.pop() || ""; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          try {
            const data = JSON.parse(line.slice(6)) as {
              error?: string;
              message?: string;
              done?: boolean;
            };

            if (data.error) {
              throw new Error(String(data.error));
            }

            // Dispatch streaming update
            dispatch(
              updateStreamingMessage({
                message: data.message || "",
                done: data.done || false,
              }),
            );

            if (data.done) {
              return { message: data.message || "", done: true };
            }
          } catch (e) {
            console.error("Error parsing stream data:", e);
          }
        }
      }
    }

    return { message: "", done: true };
  },
);

export const generateSuggestedResponse = createAsyncThunk(
  "chat/generateSuggestedResponse",
  async (
    payload: {
      contactId: string;
      modelId: string | null;
      messages: Array<{
        role: "user" | "assistant";
        content: string;
        timestamp?: string;
      }>;
    },
    { dispatch },
  ) => {
    dispatch(
      updateSuggestedResponseStreaming({
        message: "",
        done: false,
        error: null,
      }),
    );

    try {
      const streamResponse = await fetch(
        "/api/ai/generate-suggested-response",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (!streamResponse.ok) {
        const errorText = await streamResponse.text();
        throw new Error(errorText || "Failed to generate suggested response");
      }

      const reader = streamResponse.body?.getReader();
      if (!reader) {
        throw new Error("No reader available");
      }

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6)) as {
                error?: string;
                message?: string;
                done?: boolean;
              };

              if (data.error) {
                dispatch(
                  updateSuggestedResponseStreaming({
                    message: "",
                    done: true,
                    error: String(data.error),
                  }),
                );
                return { message: "", done: true, error: String(data.error) };
              }

              dispatch(
                updateSuggestedResponseStreaming({
                  message: data.message || "",
                  done: data.done || false,
                  error: null,
                }),
              );

              if (data.done) {
                return { message: data.message || "", done: true };
              }
            } catch (e) {
              console.error("Error parsing stream data:", e);
            }
          }
        }
      }

      return { message: "", done: true };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An error occurred";
      dispatch(
        updateSuggestedResponseStreaming({
          message: "",
          done: true,
          error: errorMessage,
        }),
      );
      return { message: "", done: true, error: errorMessage };
    }
  },
);

export const sendMessage = createAsyncThunk(
  "chat/sendMessage",
  async (
    payload: SaveAgentMessageParams & {
      member: TeamMember;
    },
  ) => {
    return await trpc.chat.sendMessage.mutate({
      ...payload,
    });
  },
);

export const getContacts = createAsyncThunk(
  "chat/getList",
  async (payload: {
    verified: boolean;
    q: string;
    limit: number;
    cursorId?: string | null;
  }) => {
    const { verified, q, limit, cursorId } = payload;

    return await trpc.chat.getContacts.query({
      verified: verified,
      q: q,
      limit: limit,
      cursorId: cursorId ?? null,
    });
  },
);

export const getContact = createAsyncThunk(
  "chat/getContact",
  async (payload: SearchMessageParams & { concat: boolean }) => {
    const { contactId, chatId, cursor } = payload;
    return await trpc.chat.getContact.query({
      contactId,
      chatId,
      cursor,
    });
  },
);

export const markContactRead = createAsyncThunk(
  "chat/markRead",
  async (payload: { contactId: string }) => {
    return await trpc.chat.markRead.mutate({ contactId: payload.contactId });
  },
);

export const selectSubscription = createAsyncThunk<
  unknown,
  { subscriptionId: string; contactId: string }
>("chat/selectSubscription", async (props) => {
  return (await trpc.communicationChannels.selectSubscription.mutate({
    subscriptionId: props.subscriptionId,
    contactId: props.contactId,
  })) as unknown;
});

export const createCommunicationChannel = createAsyncThunk(
  "chat/createCommunicationChannel",
  async (props: {
    name: string;
    identifierValue: string;
    // Channel handle from executable.config (skedyul.config.ts)
    handle: string;
    appInstallationId: string;
    linkedFields: string[];
  }) => {
    return await trpc.communicationChannels.createCommunicationChannel.mutate({
      name: props.name,
      identifierValue: props.identifierValue,
      handle: props.handle,
      appInstallationId: props.appInstallationId,
      linkedFields: props.linkedFields,
    });
  },
);

export const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    reset: () => {
      return initialState;
    },
    addStagedMessage: (
      state,
      action: PayloadAction<{
        message: string;
      }>,
    ) => {
      state.contact.stagedMessage.message = action.payload.message;
      state.contact.stagedMessage.isVisible = true;
      state.contact.stagedMessage.isLoading = true;
    },
    updateMessage: (state, action: PayloadAction<string>) => {
      if (!state.contact.newMessage.data) {
        state.contact.newMessage.data = {
          id: generateId("chatMessage"),
          contactId: state.contact.data?.id || "",
          memberId: null,
          chatId: null,
          message: action.payload,
          createdAt: new Date().toISOString(),
          receivedAt: null,
          sentAt: null,
        } as unknown as ListMessage;
      } else {
        state.contact.newMessage.data.message = action.payload;
      }
    },
    updateStreamingMessage: (
      state,
      action: PayloadAction<{
        message: string;
        done: boolean;
      }>,
    ) => {
      // Update the staged message with the streaming content
      state.contact.stagedMessage.message = action.payload.message;
      state.contact.stagedMessage.isVisible = true;
      state.contact.stagedMessage.isLoading = !action.payload.done;

      // If streaming is done, stop loading
      if (action.payload.done) {
        state.contact.stagedMessage.isLoading = false;
      }
    },
    sendStagedMessage: (state) => {
      // Send the staged message and hide it
      state.contact.stagedMessage.isVisible = false;
      state.contact.stagedMessage.message = "";
      state.contact.stagedMessage.isLoading = false;
      // Increment resetKey to force ChatInputBar to reset and clear selected action
      state.contact.newMessage.resetKey =
        (state.contact.newMessage.resetKey || 0) + 1;
    },
    rejectStagedMessage: (state) => {
      // Hide the staged message
      state.contact.stagedMessage.isVisible = false;
      state.contact.stagedMessage.message = "";
      state.contact.stagedMessage.isLoading = false;
      // Increment resetKey to force ChatInputBar to reset and clear selected action
      state.contact.newMessage.resetKey =
        (state.contact.newMessage.resetKey || 0) + 1;
    },
    editStagedMessage: (state, action: PayloadAction<string>) => {
      // Update the staged message content
      state.contact.stagedMessage.message = action.payload;
    },
    updateSuggestedResponseStreaming: (
      state,
      action: PayloadAction<{
        message: string;
        done: boolean;
        error: string | null;
      }>,
    ) => {
      state.contact.suggestedResponse.message = action.payload.message;
      state.contact.suggestedResponse.isVisible = true;
      state.contact.suggestedResponse.isLoading = !action.payload.done;
      state.contact.suggestedResponse.error = action.payload.error;
    },
    setSuggestedResponseModel: (state, action: PayloadAction<string>) => {
      state.contact.suggestedResponse.selectedModelId = action.payload;
    },
    editSuggestedResponse: (state, action: PayloadAction<string>) => {
      state.contact.suggestedResponse.message = action.payload;
    },
    dismissSuggestedResponse: (state) => {
      state.contact.suggestedResponse.isVisible = false;
      state.contact.suggestedResponse.message = "";
      state.contact.suggestedResponse.isLoading = false;
      state.contact.suggestedResponse.error = null;
    },
    acceptSuggestedResponse: (state) => {
      state.contact.suggestedResponse.isVisible = false;
      state.contact.suggestedResponse.message = "";
      state.contact.suggestedResponse.isLoading = false;
      state.contact.suggestedResponse.error = null;
      state.contact.newMessage.resetKey =
        (state.contact.newMessage.resetKey || 0) + 1;
    },
    setSuggestedResponseLastMessageId: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.contact.suggestedResponse.lastMessageId = action.payload;
    },
    receiveMessage: (
      state,
      action: PayloadAction<{
        contact: GetContactOutputType;
        message: ListMessage;
        workflowRunId?: string;
        member?: TeamMember;
        verified: boolean;
      }>,
    ) => {
      /** if the current contact is the one sending the message */
      const isCurrentContact =
        action.payload.contact.id === state.contact.data?.id;

      if (isCurrentContact) {
        // Append the incoming message to the currently open conversation
        const incoming = {
          ...action.payload.message,
          chatId: action.payload.message.chatId || undefined,
        } as ListMessage;

        const existingIds = new Set(
          (state.contact.data?.messages || []).map((m) => m.id),
        );
        if (!existingIds.has(incoming.id)) {
          state.contact.data = {
            ...(state.contact.data as GetContactOutputType),
            messages: [...(state.contact.data?.messages || []), incoming],
          } as GetContactOutputType;
        }

        // Move contact to top without increasing unread count
        const updatedContact: GetContactOutputType = {
          ...action.payload.contact,
          lastMessage: incoming.message,
        };
        shuffleContactToTop(state, updatedContact, false);
      } else {
        // For other contacts, move to top and increment unread when message is from contact
        shuffleContactToTop(
          state,
          action.payload.contact,
          action.payload.member?.id === undefined,
        );
      }
    },
    applyMessageStatusUpdate: (
      state,
      action: PayloadAction<{
        messageId: string;
        status: "in_flight" | "sent" | "received" | "rejected" | "delivered";
      }>,
    ) => {
      const LocalMessageStatusEventPayloadSchema = z.object({
        messageId: z.string(),
        status: z.enum([
          "in_flight",
          "sent",
          "received",
          "rejected",
          "delivered",
        ]),
      });
      const parsed = LocalMessageStatusEventPayloadSchema.parse(action.payload);
      const current = state.contact.data;
      if (!current) return;
      const messages = current.messages || [];
      const index = messages.findIndex((m) => m.id === parsed.messageId);
      if (index === -1) return;
      const target = messages[index] as unknown as Record<string, unknown>;
      const now = new Date().toISOString();
      switch (parsed.status) {
        case "sent":
          target.sentAt = target.sentAt ?? now;
          break;
        case "received":
          target.receivedAt = target.receivedAt ?? now;
          break;
        case "rejected":
          target.lastAttemptAt = now;
          break;
        case "delivered":
          // Treat delivered similar to sent when timestamp missing
          target.sentAt = target.sentAt ?? now;
          break;
        case "in_flight":
        default:
          // no-op for now; UI may reflect spinner via optimistic state
          break;
      }
      // Write back
      current.messages[index] = target as unknown as ListMessage;
    },
  },
  extraReducers: (builder) => {
    // select subscription
    builder
      .addCase(selectSubscription.fulfilled, (state, action) => {
        const { subscriptionId, contactId } = action.meta.arg;

        // Update selected flag on the currently viewed contact
        if (state.contact.data && state.contact.data.id === contactId) {
          state.contact.data.communicationChannelSubscriptions =
            state.contact.data.communicationChannelSubscriptions.map((s) =>
              s.id === subscriptionId
                ? { ...s, selected: true }
                : { ...s, selected: false },
            ) as unknown as GetContactOutputType["communicationChannelSubscriptions"];
        }

        // Update the contact in the contacts list if present
        state.contacts.data = state.contacts.data.map((c) => {
          if (c.id !== contactId) return c;
          return {
            ...c,
            communicationChannelSubscriptions:
              c.communicationChannelSubscriptions.map((s) =>
                s.id === subscriptionId
                  ? { ...s, selected: true }
                  : { ...s, selected: false },
              ),
          } as GetContactOutputType;
        });
      })
      .addCase(selectSubscription.pending, (state, action) => {
        // Optional: optimistic UI selection
        const { subscriptionId, contactId } = action.meta.arg;
        if (state.contact.data && state.contact.data.id === contactId) {
          state.contact.data.communicationChannelSubscriptions =
            state.contact.data.communicationChannelSubscriptions.map((s) =>
              s.id === subscriptionId
                ? { ...s, selected: true }
                : { ...s, selected: false },
            ) as unknown as GetContactOutputType["communicationChannelSubscriptions"];
        }
      });

    // get contacts
    builder
      .addCase(getContacts.pending, (state, action) => {
        const { cursorId, q, limit } = action.meta.arg;

        if (q !== state.contacts.query.q) {
          state.contacts.data = [];
        }

        state.contacts.isLoading = true;
        state.contacts.query.cursorId = cursorId ?? null;
        state.contacts.query.q = q;
        state.contacts.query.limit = limit;
      })
      .addCase(getContacts.fulfilled, (state, action) => {
        const meta = z
          .object({
            limit: z.number(),
            total: z.number(),
            nextCursorId: z.string().nullable().optional(),
          })
          .parse(action.payload.meta);
        const { total, nextCursorId } = meta;
        const currents = state.contacts.data;
        const isInitialLoad = (currents?.length || 0) === 0;
        state.contacts.query = {
          ...state.contacts.query,
          total,
          cursorId: nextCursorId ?? null,
        };

        const incoming = action.payload.records as GetContactOutputType[];
        if (isInitialLoad) {
          state.contacts.data = incoming;
        } else {
          const existingIds = new Set(state.contacts.data.map((c) => c.id));
          const deduped = incoming.filter((c) => !existingIds.has(c.id));
          state.contacts.data = [...state.contacts.data, ...deduped];
        }

        state.contacts.isLoading = false;
      })
      .addCase(getContacts.rejected, (state) => {
        state.contacts.isLoading = false;
      });

    // get contact
    builder
      .addCase(getContact.pending, (state, action) => {
        if (!action.meta.arg.concat) {
          state.contact.isLoading = true;
          state.contact.data = null;
        }
      })
      .addCase(getContact.fulfilled, (state, action) => {
        state.contact.isLoading = false;
        const incoming = action.payload;
        const messages = incoming.messages.map((message) => ({
          ...message,
          chatId: message.chatId || undefined,
        })) as ListMessage[];

        const isSameContact = state.contact.data?.id === incoming.id;

        if (!isSameContact) {
          // Switching contacts: load entire contact payload
          state.contact.data = {
            ...incoming,
            messages,
          } as GetContactOutputType;
        } else if (action.meta.arg.concat) {
          // Same contact and concat requested: append new unique messages
          const existingMessageIds = new Set(
            (state.contact.data?.messages || []).map((msg) => msg.id),
          );
          const newMessages = messages.filter(
            (msg) => !existingMessageIds.has(msg.id),
          );
          state.contact.data!.messages = [
            ...(state.contact.data?.messages || []),
            ...newMessages,
          ];
        } else {
          // Same contact, replace message list
          if (state.contact.data) {
            state.contact.data.messages = messages;
          } else {
            state.contact.data = {
              ...incoming,
              messages,
            } as GetContactOutputType;
          }
        }

        /** update the contact in the contacts list if it exists, but don't move it to the top */
        const existingIndex = state.contacts.data.findIndex(
          (c) => c.id === action.meta.arg.contactId,
        );
        if (existingIndex !== -1) {
          // Update the contact in place, preserving its position
          state.contacts.data[existingIndex] = action.payload;
        }
        // If contact doesn't exist in current page, don't add it (per user requirement)
      })
      .addCase(getContact.rejected, (state) => {
        state.contact.isLoading = false;
      })
      .addCase(markContactRead.fulfilled, (state, action) => {
        const contactId = (action.meta.arg as { contactId: string }).contactId;
        // Zero the unread count for the contact list entry
        state.contacts.data = state.contacts.data.map((c) =>
          c.id === contactId
            ? ({ ...c, unreadCount: 0 } as unknown as GetContactOutputType)
            : c,
        );
        // Also reflect on the currently loaded contact (if matching)
        if (state.contact.data && state.contact.data.id === contactId) {
          (
            state.contact.data as unknown as { unreadCount?: number }
          ).unreadCount = 0;
        }
      })
      .addCase(sendMessage.pending, (state, action) => {
        // Optimistically append the outgoing message and shuffle contact to top
        if (!state.contact.data) {
          return;
        }
        const selectedContact = state.contact.data;
        const member = action.meta.arg.member;
        const memberLabel =
          `${member.firstName || ""}${
            member.firstName && member.lastName ? " " : ""
          }${member.lastName || ""}`.trim() || "Member";

        const optimisticMessage = {
          id: action.meta.arg.id,
          contactId: selectedContact.id,
          memberId: action.meta.arg.member.id,
          chatId: null,
          message: action.meta.arg.message,
          createdAt: new Date().toISOString(),
          receivedAt: null,
          sentAt: null,
          lastAttemptAt: undefined,
          contentUrl: undefined,
          author: {
            referenceId: member.id,
            label: memberLabel,
            type: "MEMBER",
            image: { seed: member.id },
          },
        } as unknown as ListMessage;

        state.contact.data.messages = [
          ...(state.contact.data?.messages || []),
          optimisticMessage,
        ];

        // Update lastMessage and move the contact to the top without increasing unread count
        const updatedContact = {
          ...selectedContact,
          lastMessage: action.meta.arg.message,
        } as GetContactOutputType;
        shuffleContactToTop(state, updatedContact, false);

        // Clear the input message quickly for snappier UX
        if (state.contact.newMessage.data) {
          state.contact.newMessage.data = {
            ...(state.contact.newMessage.data as ListMessage),
            id: generateId("chatMessage"),
            message: "",
          } as unknown as ListMessage;
        }
      });
    // TODO
    // .addCase(sendMessage.rejected, (state, action) => {})
    // .addCase(sendMessage.fulfilled, (state, action) => {

    // })
  },
});

// Action creators are generated for each case reducer function
export const {
  addStagedMessage,
  receiveMessage,
  updateMessage,
  reset,
  updateStreamingMessage,
  sendStagedMessage,
  rejectStagedMessage,
  editStagedMessage,
  applyMessageStatusUpdate,
  updateSuggestedResponseStreaming,
  setSuggestedResponseModel,
  editSuggestedResponse,
  dismissSuggestedResponse,
  acceptSuggestedResponse,
  setSuggestedResponseLastMessageId,
} = chatSlice.actions;

export default chatSlice.reducer;
